// Helm card: a bot attaches a small structured payload to a message; humans see a clean card,
// and can always flip to the raw data ("view source"). Nothing is hidden.
//
// Payload (any extra keys are shown as fields):
//   { type: "status" | "handoff" | "approval" | "report" | "info",
//     title, subtitle?, status?: "pending"|"ready"|"pass"|"fail"|"error"|"done"|string,
//     fields?: { [label]: value }, link?: { label?, url }, actions?: [{ label, value }] }
import React, { useState } from 'react';
import { View, Pressable, Linking, StyleSheet } from 'react-native';
import { Text } from './Text';
import { makeStyles, useTheme, radius, Theme } from '../theme';

const ICON: Record<string, string> = { status: '◔', handoff: '↗', approval: '⚠', report: '▤', info: 'ⓘ' };
const statusColors = (t: Theme): Record<string, { fg: string; bg: string }> => ({
  pass: { fg: t.approve, bg: t.approveBg }, done: { fg: t.approve, bg: t.approveBg }, ready: { fg: t.approve, bg: t.approveBg },
  approved: { fg: t.approve, bg: t.approveBg }, sent: { fg: t.approve, bg: t.approveBg }, denied: { fg: t.deny, bg: t.denyBg }, 'sent back': { fg: t.ink, bg: t.surface2 },
  fail: { fg: t.deny, bg: t.denyBg }, error: { fg: t.deny, bg: t.denyBg },
  pending: { fg: t.warn, bg: t.agentBg }, running: { fg: t.warn, bg: t.agentBg },
});
const RESERVED = new Set(['type', 'title', 'subtitle', 'status', 'fields', 'link', 'actions', 'id', 'body', 'editable', 'locked']);

export function HelmCard({ card, onAction }: { card: any; onAction?: (value: string, label: string) => void }) {
  const theme = useTheme(); const s = useStyles();
  const [raw, setRaw] = useState(false);
  const type = String(card.type || 'info').toLowerCase();
  const status = card.status ? String(card.status).toLowerCase() : null;
  const st = status ? statusColors(theme)[status] || { fg: theme.muted, bg: theme.surface2 } : null;
  const fields: [string, string][] = [
    ...Object.entries(card.fields || {}),
    ...Object.entries(card).filter(([k, v]) => !RESERVED.has(k) && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')),
  ].map(([k, v]) => [prettyKey(k), typeof v === 'object' ? JSON.stringify(v) : String(v)]);
  const link = card.link && (typeof card.link === 'string' ? { url: card.link } : card.link);
  // An approval is decided with the Approve / Revise / Deny bar under the message, the same one a Hermes
  // gate gets, so the card's own buttons are not drawn a second time.
  const actions: { label: string; value: string }[] = type !== 'approval' && Array.isArray(card.actions) ? card.actions.filter((a: any) => a && a.label) : [];
  const draft = typeof card.body === 'string' && card.body.trim() ? card.body.trim() : null; // the thing to approve, in full
  const [all, setAll] = useState(false);
  // A settled approval keeps its text one tap away: the room would otherwise repeat every draft three times.
  const settled = type === 'approval' && !!status && !['pending', 'waiting', 'open', 'requested'].includes(status);
  const [shown, setShown] = useState(false);

  return (
    <View style={s.card}>
      <View style={s.head}>
        <Text style={s.icon}>{ICON[type] || ICON.info}</Text>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={s.title} numberOfLines={2}>{card.title || prettyKey(type)}</Text>
          {!!card.subtitle && <Text style={s.sub} numberOfLines={3}>{String(card.subtitle)}</Text>}
        </View>
        {st && <View style={[s.pill, { backgroundColor: st.bg }]}><Text style={[s.pillText, { color: st.fg }]}>{status}</Text></View>}
      </View>
      {fields.length > 0 && (
        <View style={s.fields}>
          {fields.map(([k, v]) => (
            <View key={k} style={s.row}><Text style={s.k}>{k}</Text><Text style={s.v} numberOfLines={3} selectable>{v}</Text></View>
          ))}
        </View>
      )}
      {draft && settled && !shown && (
        <Pressable onPress={() => setShown(true)} hitSlop={6} style={{ marginTop: 8 }}><Text style={s.srcText}>Show the text</Text></Pressable>
      )}
      {draft && (!settled || shown) && (
        <View style={s.draft}>
          <Text style={s.draftText} selectable numberOfLines={all ? undefined : 12}>{draft}</Text>
          {draft.split('\n').length > 12 || draft.length > 700 ? (
            <Pressable onPress={() => setAll((v) => !v)} hitSlop={6}><Text style={s.srcText}>{all ? 'Show less' : 'Show all'}</Text></Pressable>
          ) : null}
        </View>
      )}
      {(link || actions.length > 0) && (
        <View style={s.actions}>
          {actions.map((a) => (
            <Pressable key={a.label} onPress={() => onAction?.(String(a.value ?? a.label), a.label)} style={({ pressed }) => [s.act, pressed && { opacity: 0.6 }]}>
              <Text style={s.actText}>{a.label}</Text>
            </Pressable>
          ))}
          {link?.url && (
            <Pressable onPress={() => Linking.openURL(String(link.url)).catch(() => {})} style={({ pressed }) => [s.act, s.linkAct, pressed && { opacity: 0.6 }]}>
              <Text style={[s.actText, { color: theme.accent }]}>{link.label || 'Open'} ↗</Text>
            </Pressable>
          )}
        </View>
      )}
      <Pressable onPress={() => setRaw((v) => !v)} hitSlop={6} style={s.srcBtn}>
        <Text style={s.srcText}>{raw ? 'Hide source' : 'View source'}</Text>
      </Pressable>
      {raw && <Text style={s.src} selectable>{JSON.stringify(card, null, 2)}</Text>}
    </View>
  );
}

function prettyKey(k: string) { return k.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase()); }

const useStyles = makeStyles((theme) => StyleSheet.create({
  card: { marginTop: 6, backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border, borderRadius: radius.lg, padding: 12, gap: 8, maxWidth: 360 },
  head: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  icon: { color: theme.accent, fontSize: 18, lineHeight: 22, fontFamily: theme.mono },
  title: { color: theme.ink, fontSize: 15, fontWeight: '700', lineHeight: 20 },
  sub: { color: theme.faint, fontSize: 12, marginTop: 2, fontFamily: theme.mono },
  pill: { borderRadius: radius.pill, paddingHorizontal: 8, paddingVertical: 3 },
  pillText: { fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: theme.mono },
  fields: { gap: 4 },
  row: { flexDirection: 'row', gap: 10 },
  k: { color: theme.faint, fontSize: 12.5, width: 96, fontFamily: theme.mono },
  v: { color: theme.ink, fontSize: 13.5, flex: 1 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 2 },
  act: { backgroundColor: theme.surface2, borderWidth: 1, borderColor: theme.border, borderRadius: radius.md, paddingHorizontal: 12, paddingVertical: 8 },
  linkAct: { borderColor: theme.accentBorder },
  actText: { color: theme.ink, fontWeight: '700', fontSize: 13 },
  srcBtn: { alignSelf: 'flex-start', marginTop: 2 },
  srcText: { color: theme.faint, fontSize: 11.5, fontFamily: theme.mono },
  draft: { marginTop: 10, backgroundColor: theme.bg, borderRadius: radius.md, borderWidth: 1, borderColor: theme.border, padding: 12, gap: 8 },
  draftText: { color: theme.ink, fontSize: 14.5, lineHeight: 21 },
  src: { color: theme.muted, fontSize: 11.5, fontFamily: theme.mono, backgroundColor: theme.bg, borderRadius: radius.sm, padding: 8 },
}));
